import "shared/ui/topbar/topbar.css";

import { apm } from "@elastic/apm-rum";
import {
  BlurOn,
  DescriptionOutlined,
  ModelTrainingOutlined,
  NewReleasesOutlined,
  SchoolOutlined,
  Shortcut,
  Support,
} from "@mui/icons-material";
import { Menu, MenuItem, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { topbarStore } from "shared/common/stores/topbarStore";
import MenuWithPointerForwarding from "shared/ui/muiComponentWrappers/MenuWithPointerForwarding";

interface Props {
  handleSupport: () => void;
  handleShortcut: () => void;
  role: string;
}

export default function HelpMenu(props: Props) {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeShowIntroduction = defaultStore(
    (state) => state.changeShowIntroduction
  );
  const clientConfig = defaultStore((state) => state.clientConfig);
  const reportAnonymization = defaultStore(
    (state) => state.reportAnonymization
  );
  const changeReportAnonymization = defaultStore(
    (state) => state.changeReportAnonymization
  );
  const changeShowWhatsNew = defaultStore((state) => state.changeShowWhatsNew);

  const anchorHelp = topbarStore((state) => state.anchorHelp);
  const showHelp = topbarStore((state) => state.showHelp);
  const changeShowHelp = topbarStore((state) => state.changeShowHelp);

  const { t } = useTranslation();
  // Help id
  const HelpId = "Help";

  //restart onboarding steps
  const handleStorage = () => {
    changeShowHelp(false);
    changeShowIntroduction(true);
  };

  //open the report anonymization window
  const handleReportAnonymization = () => {
    changeReportAnonymization(!reportAnonymization);
  };

  const handleWhatsNew = () => {
    changeShowHelp(false);
    changeShowWhatsNew(true);
  };

  return (
    <MenuWithPointerForwarding
      anchorEl={anchorHelp}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={HelpId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        padding: 0,
        marginTop: "6px",
      }}
      open={showHelp}
      onClose={() => changeShowHelp(false)}
      classes={{
        root: "indexForSupport",
        list: darkTheme ? "infra3d-menuListDark" : "infra3d-menuListLight",
      }}
    >
      <MenuItem
        onClick={() =>
          monitor(apm, "open-knowledge-base | Support", () =>
            window.open("https://help.infra3d.com/", "_blank")
          )
        }
        sx={{
          gap: "10px",
          whiteSpace: "normal",
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
          },
        }}
        className="onboarding-knowledgeBase"
      >
        <DescriptionOutlined />
        <Typography>{t("support_menu.user_manual")}</Typography>
      </MenuItem>
      <MenuItem
        onClick={() =>
          monitor(apm, "open-academy | Academy", () =>
            window.open("https://events.infra3d.com/de/academy", "_blank")
          )
        }
        sx={{
          gap: "10px",
          whiteSpace: "normal",
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
          },
        }}
      >
        <SchoolOutlined />
        <Typography>infra3D Academy</Typography>
      </MenuItem>
      <MenuItem
        onClick={() =>
          monitor(apm, "open-support-drawer | Support", props.handleSupport)
        }
        sx={{
          gap: "10px",
          whiteSpace: "normal",
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
          },
        }}
      >
        <Support />
        <Typography>{t("support_menu.support")}</Typography>
      </MenuItem>
      <MenuItem
        onClick={() =>
          monitor(apm, "open-shortcut-drawer | Support", props.handleShortcut)
        }
        sx={{
          gap: "10px",
          whiteSpace: "normal",
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
          },
        }}
        className="onboarding-shortcut"
      >
        <Shortcut />
        <Typography>{t("support_menu.shortcuts")}</Typography>
      </MenuItem>
      {clientConfig?.anon_project && (
        <MenuItem
          onClick={() =>
            monitor(apm, "open-ria-workflow | Support", () => {
              changeShowHelp(false);
              handleReportAnonymization();
            })
          }
          sx={{
            gap: "10px",
            whiteSpace: "normal",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          <BlurOn />
          <Typography>{t("support_menu.ria")}</Typography>
        </MenuItem>
      )}
      {props.role !== "api" && (
        <MenuItem
          onClick={() =>
            monitor(apm, "restart-onboarding | Support", handleStorage)
          }
          sx={{
            gap: "10px",
            whiteSpace: "normal",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          <ModelTrainingOutlined />
          <Typography>{t("support_menu.onboarding")}</Typography>
        </MenuItem>
      )}
      {props.role !== "api" && (
        <MenuItem
          sx={{
            gap: "10px",
            whiteSpace: "normal",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          onClick={() =>
            monitor(apm, "click-whats-new | Settings", handleWhatsNew)
          }
        >
          {<NewReleasesOutlined />}
          <Typography variant="body1">{t("usermenu.news")}</Typography>
        </MenuItem>
      )}
    </MenuWithPointerForwarding>
  );
}
